import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { YButton } from '../../../ui/YButton';
import { PlaceSearchInput } from '../../../components/ui/PlaceSearchInput';
import { toolkitService } from '../services/toolkitService';
import { MAPBOX_TOKEN } from '../../../utils/constants';
import { getSavedStores, saveStore, storeId } from '../../../utils/savedStoresStorage';
import { COMMUNITY_SUGGEST_STORE_PATH } from '../../community/constants/communityRoutes';
import './SeedFeedFinder.css';

const RADIUS_CHOICES = [10, 25, 50, 75, 100];
const DEFAULT_CENTER = { latitude: 32.609, longitude: -85.480 };

if (typeof mapboxgl !== 'undefined' && MAPBOX_TOKEN && !mapboxgl.accessToken) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
}

function readHistory(key) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function saveHistory(key, entry) {
  if (typeof window === 'undefined') return [];
  try {
    const previous = readHistory(key);
    const next = [entry, ...previous].slice(0, 4);
    window.localStorage.setItem(key, JSON.stringify(next));
    return next;
  } catch (_) {
    return [];
  }
}

function splitValues(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item).trim()).filter(Boolean);
  }
  return String(raw)
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

function FinderMapView({ center, results }) {
  const containerRef = React.useRef(null);
  const mapRef = React.useRef(null);
  const markersRef = React.useRef([]);

  React.useEffect(() => {
    if (!containerRef.current) return undefined;
    if (mapRef.current) return undefined;
    if (typeof window === 'undefined' || !MAPBOX_TOKEN || !mapboxgl?.Map) return undefined;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [center.longitude, center.latitude],
      zoom: 8,
      attributionControl: false,
    });
    mapRef.current = map;
    return () => {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, [center.latitude, center.longitude]);

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    const bounds = new mapboxgl.LngLatBounds();
    let hasBounds = false;
    results.forEach((store, index) => {
      const coords = store?.coordinates || {};
      const lat = coords.latitude;
      const lng = coords.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        return;
      }
      const el = document.createElement('div');
      el.className = 'y-finder__marker';
      el.textContent = String(index + 1);
      const marker = new mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(map);
      markersRef.current.push(marker);
      bounds.extend([lng, lat]);
      hasBounds = true;
    });
    if (hasBounds) {
      map.fitBounds(bounds, { padding: 56, duration: 500, maxZoom: 10 });
    } else {
      map.easeTo({ center: [center.longitude, center.latitude], zoom: 8 });
    }
  }, [results, center]);

  return <div className="y-finder__map" ref={containerRef} />;
}

FinderMapView.propTypes = {
  center: PropTypes.shape({
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }).isRequired,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function SeedFeedFinder({
  variant = 'feed',
  feedOptions = [],
  seedOptions = [],
  defaultRadius = 25,
  storageKey = 'y-toolkit-find-stores',
  appearance = 'default',
}) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [selectedLocation, setSelectedLocation] = React.useState(null);
  const [radius, setRadius] = React.useState(defaultRadius);
  const [needFeed, setNeedFeed] = React.useState(true);
  const [needSeed, setNeedSeed] = React.useState(variant === 'area');
  const [feedFocus, setFeedFocus] = React.useState(feedOptions[0]?.value || '');
  const [seedFocus, setSeedFocus] = React.useState(seedOptions[0]?.value || '');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [meta, setMeta] = React.useState(null);
  const [history, setHistory] = React.useState(() => readHistory(storageKey));
  const [view, setView] = React.useState('list');
  const [savedIds, setSavedIds] = React.useState(() => new Set());
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    getSavedStores()
      .then((list) => {
        if (!cancelled) {
          setSavedIds(new Set(list.map((s) => s.store_id || storeId(s))));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const mapCenter = React.useMemo(() => {
    if (results.length > 0) {
      const coords = results[0].coordinates || {};
      if (typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
        return { latitude: coords.latitude, longitude: coords.longitude };
      }
    }
    if (selectedLocation) {
      return {
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
      };
    }
    return DEFAULT_CENTER;
  }, [results, selectedLocation]);

  const handlePlaceSelect = (place) => {
    if (!place) return;
    setSelectedLocation(place);
    setQuery(place.label || place.name);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedLocation) {
      setError('Select a city or town first.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const payload = {
        location: {
          place_id: selectedLocation.id,
          name: selectedLocation.label || selectedLocation.name || '',
          latitude: selectedLocation.latitude,
          longitude: selectedLocation.longitude,
        },
        radius_miles: radius,
        needs: {
          feed_types: needFeed ? splitValues(feedFocus) : [],
          seed_types: needSeed ? splitValues(seedFocus) : [],
        },
      };
      const response = await toolkitService.findSupplyStores(payload);
      const responseResults = Array.isArray(response?.results) ? response.results : [];
      setResults(responseResults);
      setMeta(response?.meta || null);
      setView(responseResults.length > 0 ? 'list' : 'list');
      const entry = {
        label: selectedLocation.label || selectedLocation.name,
        location: selectedLocation,
        radius,
        needs: payload.needs,
        ts: Date.now(),
      };
      const nextHistory = saveHistory(storageKey, entry);
      setHistory(nextHistory);
    } catch (_) {
      setResults([]);
      setMeta(null);
      setError('Unable to load stores right now.');
    } finally {
      setLoading(false);
    }
  };

  const handleHistorySelect = (entry) => {
    if (!entry?.location) return;
    setSelectedLocation(entry.location);
    setQuery(entry.location.label || entry.location.name || '');
    setRadius(entry.radius || defaultRadius);
    setNeedFeed((entry.needs?.feed_types || []).length > 0);
    setNeedSeed((entry.needs?.seed_types || []).length > 0);
    setFeedFocus(entry.needs?.feed_types?.join(', ') || '');
    setSeedFocus(entry.needs?.seed_types?.join(', ') || '');
  };

  const resultsView = view === 'map' && MAPBOX_TOKEN ? (
    <FinderMapView center={mapCenter} results={results} />
  ) : (
    <div className="y-finder__results">
      {results.length === 0 ? (
        <div className="text-muted small">No stores yet. Select a city and search to see results.</div>
      ) : (
        results.map((store, index) => (
          <div key={store.id || `${store.name}-${index}`} className="y-finder__result-card">
            <div className="y-finder__result-header">
              <div>
                <div className="y-finder__result-rank">{index + 1}</div>
                <div className="y-finder__result-name">{store.name || 'Feed & seed store'}</div>
              </div>
              <div className="text-muted small">
                {typeof store.distance_miles === 'number' ? `${store.distance_miles.toFixed(1)} mi` : ''}
              </div>
            </div>
            <div className="y-finder__result-badges">
              {store.priority_label && (
                <span className="y-finder__badge">{store.priority_label}</span>
              )}
              {store.brand && (
                <span className="y-finder__badge badge-outline">{store.brand}</span>
              )}
              {store.source && (
                <span className="y-finder__badge badge-outline">{store.source}</span>
              )}
            </div>
            <div className="text-muted small">
              {store.address || `${store.city || ''}${store.state ? `, ${store.state}` : ''}`}
            </div>
            <div className="text-muted small">
              {[store.contact?.phone, store.contact?.email].filter(Boolean).join(' · ')}
            </div>
            <div className="d-flex gap-2 flex-wrap mt-2">
              <button
                type="button"
                className={`y-btn y-btn-secondary y-btn-sm ${savedIds.has(storeId(store)) ? 'text-success' : ''}`}
                onClick={async () => {
                  await saveStore(store);
                  setSavedIds((prev) => new Set([...prev, storeId(store)]));
                }}
                disabled={savedIds.has(storeId(store))}
              >
                {savedIds.has(storeId(store)) ? 'Saved' : 'Save to My Stores'}
              </button>
              {store.urls?.website && (
                <a className="y-btn y-btn-secondary y-btn-sm" href={store.urls.website} target="_blank" rel="noreferrer">
                  Website
                </a>
              )}
              {store.urls?.directions && (
                <a className="y-btn y-btn-secondary y-btn-sm" href={store.urls.directions} target="_blank" rel="noreferrer">
                  Directions
                </a>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div
      className={`y-finder${appearance === 'plant-asst' || appearance === 'feed-mgmt' ? ' y-finder--plant-asst' : ''}`}
      ref={containerRef}
    >
      <form className="y-finder__form" onSubmit={handleSubmit}>
        <label className="form-label" htmlFor="finder-city-input">Where do you need supplies?</label>
        <div className="y-finder__field">
          <PlaceSearchInput
            inputId="finder-city-input"
            value={query}
            onChange={setQuery}
            onSelect={handlePlaceSelect}
            placeholder="Search city, town, or address"
            limit={6}
            aria-label="Search city or town for nearby stores"
          />
        </div>

        <div className="y-finder__controls">
          <div>
            <label className="form-label" htmlFor="finder-radius-select">Search radius</label>
            <select
              id="finder-radius-select"
              className="form-select"
              value={radius}
              onChange={(event) => setRadius(Number(event.target.value))}
            >
              {RADIUS_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {choice} miles
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label d-block">Needs</label>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="finder-feed-toggle"
                checked={needFeed}
                onChange={(event) => setNeedFeed(event.target.checked)}
              />
              <label className="form-check-label" htmlFor="finder-feed-toggle">Feed</label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="checkbox"
                id="finder-seed-toggle"
                checked={needSeed}
                onChange={(event) => setNeedSeed(event.target.checked)}
              />
              <label className="form-check-label" htmlFor="finder-seed-toggle">Seed</label>
            </div>
          </div>
        </div>

        {needFeed && (
          <div className="y-finder__field">
            <label className="form-label" htmlFor="finder-feed-focus">Feed focus</label>
            {feedOptions.length > 0 ? (
              <select
                id="finder-feed-focus"
                className="form-select"
                value={feedFocus}
                onChange={(event) => setFeedFocus(event.target.value)}
              >
                <option value="">Any feed</option>
                {feedOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="finder-feed-focus"
                className="form-control"
                value={feedFocus}
                onChange={(event) => setFeedFocus(event.target.value)}
                placeholder="Corn, protein, bran…"
              />
            )}
          </div>
        )}

        {needSeed && (
          <div className="y-finder__field">
            <label className="form-label" htmlFor="finder-seed-focus">Seed focus</label>
            {seedOptions.length > 0 ? (
              <select
                id="finder-seed-focus"
                className="form-select"
                value={seedFocus}
                onChange={(event) => setSeedFocus(event.target.value)}
              >
                <option value="">Any seed mix</option>
                {seedOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="finder-seed-focus"
                className="form-control"
                value={seedFocus}
                onChange={(event) => setSeedFocus(event.target.value)}
                placeholder="Rye, clover, brassica…"
              />
            )}
          </div>
        )}

        <div className="d-flex align-items-center gap-3">
          <YButton type="submit" variant="secondary" disabled={loading}>
            {loading ? 'Searching…' : 'Find stores'}
          </YButton>
          {selectedLocation && (
            <div className="text-muted small">
              Searching near <strong>{selectedLocation.label || selectedLocation.name}</strong>
            </div>
          )}
        </div>
        {error && <div className="text-danger small mt-2">{error}</div>}
      </form>

      {results.length > 0 && (
        <div className="y-finder__view-toggle">
          <button
            type="button"
            className={`y-pill-button ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`y-pill-button ${view === 'map' ? 'active' : ''}`}
            onClick={() => setView('map')}
            disabled={!MAPBOX_TOKEN}
          >
            View map
          </button>
        </div>
      )}

      {meta && (
        <div className="y-finder__meta">
          <div className="text-muted small">
            {meta.curated_count} local partners · {meta.mapbox_count} map matches · Radius {meta.radius_miles} mi
          </div>
          {meta.attribution && (
            <div className="text-muted tiny">{meta.attribution}</div>
          )}
        </div>
      )}

      <div className="y-finder__suggest mt-2">
        <YButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => navigate(COMMUNITY_SUGGEST_STORE_PATH)}
        >
          Suggest a store
        </YButton>
      </div>

      {resultsView}

      {history.length > 0 && (
        <div className="y-finder__history">
          <div className="fw-semibold mb-2">Recent searches</div>
          <div className="d-flex flex-wrap gap-2">
            {history.map((entry) => (
              <button
                key={entry.ts}
                type="button"
                className="y-btn y-btn-secondary y-btn-sm"
                onClick={() => handleHistorySelect(entry)}
              >
                {entry.label} · {entry.radius} mi
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

SeedFeedFinder.propTypes = {
  variant: PropTypes.oneOf(['feed', 'area']),
  feedOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  seedOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  defaultRadius: PropTypes.number,
  storageKey: PropTypes.string,
  appearance: PropTypes.oneOf(['default', 'plant-asst', 'feed-mgmt']),
};

export default SeedFeedFinder;

















